import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Circle, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Search, MapPin, Loader2, Maximize2, Minimize2 } from 'lucide-react';

// Mappa per configurare il geofence di un servizio fiduciario: tile OpenStreetMap,
// ricerca indirizzo con Nominatim (limite 1 req/s, sufficiente per questo uso),
// marker trascinabile e cerchio del raggio scelto.
//
// Con CRA/webpack le icone di default di Leaflet puntano a path inesistenti:
// vanno reimpostate a mano.
const DefaultIcon = L.icon({
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

// Icona marker rossa al posto di quella di default.
const RedIcon = L.divIcon({
    className: 'geofence-red-marker',
    html: `<div style="
        width: 22px; height: 22px; border-radius: 50%;
        background: #dc2626; border: 3px solid #ffffff;
        box-shadow: 0 0 0 2px #dc2626, 0 2px 6px rgba(0,0,0,0.3);
    "></div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
});

const RAGGI_DISPONIBILI = [10, 50, 100, 200, 500];

export interface GeofenceValue {
    lat: number;
    lng: number;
    raggioMt: number;
}

interface Props {
    value: GeofenceValue | null;
    onChange: (v: GeofenceValue) => void;
    // Indirizzo del servizio, usato per precompilare la ricerca.
    initialAddress?: string;
}

// Centro Italia di default.
const FALLBACK_CENTER: [number, number] = [41.8719, 12.5674];
const FALLBACK_ZOOM = 6;

// Forza re-pan della mappa quando cambia il centro target (dopo geocoding o reset).
const CenterUpdater: React.FC<{ center: [number, number] | null; zoom?: number }> = ({ center, zoom }) => {
    const map = useMap();
    useEffect(() => {
        if (center) {
            map.setView(center, zoom ?? map.getZoom(), { animate: true });
        }
    }, [center, zoom, map]);
    return null;
};

// Il click sulla mappa posiziona il marker. Al cambio fullscreen serve invalidateSize,
// altrimenti Leaflet non ricalcola i tile e parte della mappa resta grigia.
const MapClickHandler: React.FC<{ onClick: (lat: number, lng: number) => void }> = ({ onClick }) => {
    useMapEvents({
        click: (e) => onClick(e.latlng.lat, e.latlng.lng),
    });
    return null;
};

const MapResizer: React.FC<{ trigger: unknown }> = ({ trigger }) => {
    const map = useMap();
    // Solo al mount: Leaflet può inizializzarsi su un container non ancora
    // dimensionato e i pane interni allungano poi la pagina.
    useEffect(() => {
        const t = setTimeout(() => map.invalidateSize(), 100);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        const t = setTimeout(() => map.invalidateSize(), 50);
        return () => clearTimeout(t);
    }, [trigger, map]);
    return null;
};

interface NominatimResult {
    lat: string;
    lon: string;
    display_name: string;
}

export const GeofenceMapPicker: React.FC<Props> = ({ value, onChange, initialAddress }) => {
    const [query, setQuery] = useState<string>(initialAddress || '');
    const [results, setResults] = useState<NominatimResult[]>([]);
    const [searching, setSearching] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [centerTarget, setCenterTarget] = useState<[number, number] | null>(null);
    const [fullscreen, setFullscreen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const raggio = value?.raggioMt ?? 100;
    // Il marker è sempre visibile, così l'utente sa cosa trascinare. Se value è
    // null è una bozza attenuata al centro di default, confermata al primo
    // trascinamento o click.
    const markerPos: [number, number] = value ? [value.lat, value.lng] : FALLBACK_CENTER;
    const initialCenter: [number, number] = value ? [value.lat, value.lng] : FALLBACK_CENTER;
    const initialZoom = value ? 16 : FALLBACK_ZOOM;
    const hasValue = !!value;

    // Chiusura click-fuori
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const runSearch = async () => {
        if (!query.trim()) return;
        setSearching(true);
        try {
            const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=5&addressdetails=1`;
            const res = await fetch(url, {
                headers: { 'Accept': 'application/json' },
            });
            const data: NominatimResult[] = await res.json();
            setResults(data);
            setShowDropdown(true);
        } catch {
            setResults([]);
        } finally {
            setSearching(false);
        }
    };

    const pickResult = (r: NominatimResult) => {
        const lat = parseFloat(r.lat);
        const lng = parseFloat(r.lon);
        setCenterTarget([lat, lng]);
        onChange({ lat, lng, raggioMt: raggio });
        setShowDropdown(false);
    };

    const handleMarkerDragEnd = (e: L.DragEndEvent) => {
        const m = e.target as L.Marker;
        const ll = m.getLatLng();
        onChange({ lat: ll.lat, lng: ll.lng, raggioMt: raggio });
    };

    const handleMapClick = (lat: number, lng: number) => {
        onChange({ lat, lng, raggioMt: raggio });
    };

    const handleRaggioChange = (mt: number) => {
        if (!value) return;
        onChange({ ...value, raggioMt: mt });
    };

    // ESC esce dal fullscreen.
    useEffect(() => {
        if (!fullscreen) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setFullscreen(false); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [fullscreen]);

    return (
        // overflow-hidden evita che gli elementi interni di Leaflet allunghino la pagina.
        // Non usare `contain`: renderebbe il wrapper il riferimento di `position: fixed`
        // e il fullscreen non coprirebbe più il viewport.
        <div className="space-y-3 overflow-hidden">
            {/* Barra ricerca indirizzo */}
            <div className="relative" ref={dropdownRef}>
                <div className="flex gap-2">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); runSearch(); } }}
                            placeholder="Cerca indirizzo (es. Via Roma 10, Milano)"
                            className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={runSearch}
                        disabled={searching || !query.trim()}
                        className="px-3 py-2 text-sm font-bold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                    >
                        {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                        Cerca
                    </button>
                </div>
                {showDropdown && results.length > 0 && (
                    <div className="absolute z-[1100] mt-1 w-full bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden max-h-60 overflow-y-auto">
                        {results.map((r, i) => (
                            <button
                                key={`${r.lat}-${r.lon}-${i}`}
                                type="button"
                                onClick={() => pickResult(r)}
                                className="w-full text-left px-3 py-2 hover:bg-indigo-50 border-b border-gray-50 last:border-b-0 text-sm"
                            >
                                <div className="flex items-start gap-2">
                                    <MapPin size={14} className="text-red-500 shrink-0 mt-0.5" />
                                    <span className="text-gray-700">{r.display_name}</span>
                                </div>
                            </button>
                        ))}
                    </div>
                )}
                {showDropdown && !searching && results.length === 0 && query.trim() && (
                    <div className="absolute z-[1100] mt-1 w-full bg-white rounded-lg shadow-xl border border-gray-200 p-3 text-sm text-gray-500">
                        Nessun risultato.
                    </div>
                )}
            </div>

            {/* Mappa. L'attribuzione OSM, obbligatoria, è nella riga sotto la mappa
                invece che nel controllo di Leaflet. Fullscreen con fixed inset-0. */}
            <div
                className={
                    fullscreen
                        ? 'fixed inset-0 z-[2000] bg-black'
                        : 'h-72 rounded-lg overflow-hidden border border-gray-200 relative'
                }
            >
                <button
                    type="button"
                    onClick={() => setFullscreen(f => !f)}
                    className="absolute top-2 right-2 z-[1001] bg-white rounded-lg shadow-md p-2 hover:bg-gray-50 border border-gray-200"
                    title={fullscreen ? 'Esci da schermo intero (Esc)' : 'Schermo intero'}
                    aria-label={fullscreen ? 'Esci da schermo intero' : 'Schermo intero'}
                >
                    {fullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                </button>
                <MapContainer
                    center={initialCenter}
                    zoom={initialZoom}
                    style={{ height: '100%', width: '100%' }}
                    scrollWheelZoom
                    attributionControl={false}
                >
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    <CenterUpdater center={centerTarget} zoom={16} />
                    <MapClickHandler onClick={handleMapClick} />
                    <MapResizer trigger={fullscreen} />
                    <Marker
                        position={markerPos}
                        icon={RedIcon}
                        draggable
                        opacity={hasValue ? 1 : 0.7}
                        eventHandlers={{ dragend: handleMarkerDragEnd }}
                    />
                    <Circle
                        center={markerPos}
                        radius={raggio}
                        pathOptions={{
                            color: '#dc2626',
                            fillColor: '#dc2626',
                            fillOpacity: hasValue ? 0.15 : 0.08,
                            weight: 2,
                            dashArray: hasValue ? undefined : '4 4',
                        }}
                    />
                </MapContainer>
            </div>
            <p className="text-[10px] text-gray-400 text-right pr-1">
                © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer" className="hover:underline">OpenStreetMap</a> contributors
            </p>

            {/* Raggio */}
            <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">Raggio (metri)</label>
                <div className="flex flex-wrap gap-2">
                    {RAGGI_DISPONIBILI.map(mt => {
                        const active = raggio === mt;
                        return (
                            <button
                                key={mt}
                                type="button"
                                onClick={() => handleRaggioChange(mt)}
                                disabled={!value}
                                className={`px-3 py-1.5 rounded-lg text-sm font-bold border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                                    active
                                        ? 'bg-indigo-600 text-white border-indigo-600'
                                        : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                                }`}
                            >
                                {mt} m
                            </button>
                        );
                    })}
                </div>
                {!value && (
                    <p className="text-xs text-gray-400 mt-1.5">Cerca un indirizzo, clicca un punto sulla mappa o trascina il marker rosso per posizionare il geofence.</p>
                )}
                {value && (
                    <p className="text-xs text-gray-500 mt-1.5">
                        Centro: {value.lat.toFixed(6)}, {value.lng.toFixed(6)} · Trascina il marker rosso per regolare.
                    </p>
                )}
            </div>
        </div>
    );
};

export default GeofenceMapPicker;
